import React, {ReactNode} from 'react';

import {WithRouterProps} from 'react-router';

import {LinkPagination} from 'app/components/general/LinkPagination';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {LoadingList, LoadingListProps} from 'app/general/loading/LoadingList';
import {convertListResponseToStoredResponse} from 'app/tasks/components/TasksDashboard/AllTasksList/TasksLinkPagination';

import type {UseListQueryType} from 'app/store/sweet-api/api-query';

interface Props<
  ItemType,
  DashboardParams,
  FetchParams extends {page: number | string}
> extends WithRouterProps {
  useDataHook: UseListQueryType<FetchParams, ItemType>;
  hookParams: DashboardParams;
  renderItem: (item: ItemType, refresh: () => void) => ReactNode;
  hookOptions?: {refetchOnMountOrArgChange?: boolean | number};
  loadingListProps?: LoadingListProps;
  messages: {
    counter?: (count: number) => ReactNode;
    notFound:
      | {
          title: () => string;
        }
      | ((opts: {NoResultsMessage: typeof NoResultsMessage}) => ReactNode);
  };
  convertParamsFunc: (dashboardParams: DashboardParams) => FetchParams;
}

export function GenericHookPaginatedList<
  ItemType,
  DashboardParams,
  FetchParams extends {page: number | string}
>({
  useDataHook,
  hookParams,
  renderItem,
  hookOptions,
  loadingListProps,
  messages,
  location,
  convertParamsFunc,
}: Props<ItemType, DashboardParams, FetchParams>) {
  const params = convertParamsFunc(location.query);

  const {
    data: response,
    isFetching,
    refetch,
  } = useDataHook({...params, ...hookParams}, hookOptions);

  return (
    <>
      {response == null ||
      // avoid showing NoResultsMessage for the previous page data
      (response != null && response.count == 0 && isFetching) ? (
        <LoadingList {...loadingListProps} />
      ) : response != null && response.count > 0 ? (
        response.results.map((item) => renderItem(item, refetch))
      ) : response != null && response.count == 0 ? (
        // not found
        typeof messages.notFound == 'function' ? (
          messages.notFound({NoResultsMessage})
        ) : (
          <NoResultsMessage head={messages.notFound.title()} />
        )
      ) : null}

      {response != null ? (
        <LinkPagination
          className="mt-3"
          page={params.page}
          response={convertListResponseToStoredResponse(response, params.page)}
        />
      ) : null}
    </>
  );
}
